import { Eye } from "lucide-react";
import type { ReactNode } from "react";
import { Badge } from "../atoms/Badge";
import { Button } from "../atoms/Button";
import { Hash } from "../atoms/Hash";
import { Heading } from "../atoms/Heading";
import { Text } from "../atoms/Text";
import { cn } from "../lib/cn";
import { humanizeEventType } from "../lib/vocab";
import { CopyButton } from "../molecules/CopyButton";
import { TrustBadge, deriveTrustStatus } from "../molecules/TrustBadge";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "../primitives/Tooltip";

export type ActorRole =
	| "user_of_record"
	| "other_user"
	| "system"
	| "integration"
	| "unknown";

export interface EventCardProps {
	eventType: string;
	respaCode: string;
	respaName: string;
	hash: string;
	timestamp: string;
	verified: boolean;
	anchored: boolean;
	/**
	 * Whether the actor who recorded this event is the user-of-record.
	 * Null when not applicable (system events). Retained for API stability;
	 * provenance copy is driven by {@link ActorRole}.
	 */
	actorIsUserOfRecord?: boolean | null;
	/**
	 * Role classification of the actor who recorded this event.
	 */
	actorRole?: ActorRole;
	onView?: () => void;
	onCopy?: () => void;
	className?: string;
}

/**
 * Neutral, plain-language provenance chips. Recording an event from somewhere
 * other than the user-of-record is normal — never an integrity problem — so
 * these stay calm and explanatory. Red is reserved for genuine tamper issues.
 * Roles absent from this map (user_of_record, unknown) render no chip.
 */
const ACTOR_PROVENANCE: Partial<
	Record<ActorRole, { label: string; tooltip: string }>
> = {
	other_user: {
		label: "Recorded by another party",
		tooltip:
			"Another authorized party recorded this event. This is normal provenance, not a problem.",
	},
	system: {
		label: "Recorded automatically",
		tooltip:
			"The platform recorded this event automatically. This is normal provenance, not a problem.",
	},
	integration: {
		label: "Recorded via integration",
		tooltip:
			"A connected integration recorded this event. This is normal provenance, not a problem.",
	},
};

function EventCard({
	eventType,
	respaCode,
	respaName,
	hash,
	timestamp,
	verified,
	anchored,
	actorRole,
	onView,
	onCopy,
	className,
}: EventCardProps): ReactNode {
	const provenance = actorRole ? ACTOR_PROVENANCE[actorRole] : undefined;

	return (
		<div
			className={cn(
				"flex flex-col gap-3 rounded-lg border border-border bg-card p-4 sm:flex-row sm:items-start sm:justify-between",
				className,
			)}
		>
			<div className="flex flex-col gap-2">
				<div className="flex flex-wrap items-center gap-2">
					<Heading as="h3" size="md">
						{humanizeEventType(eventType)}
					</Heading>
					<TrustBadge status={deriveTrustStatus({ verified, anchored })} />
					{provenance && (
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger asChild={true}>
									<Badge variant="secondary">{provenance.label}</Badge>
								</TooltipTrigger>
								<TooltipContent side="top">
									<span className="block max-w-xs text-xs">
										{provenance.tooltip}
									</span>
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					)}
				</div>
				<div className="flex flex-wrap items-center gap-x-3 gap-y-1">
					<Text
						as="span"
						size="sm"
						textColor="mutedForeground"
						title={respaCode}
					>
						{respaName}
					</Text>
					<Text as="span" size="sm" textColor="mutedForeground">
						{timestamp}
					</Text>
				</div>
				<details className="text-xs">
					<summary className="cursor-pointer select-none text-mutedForeground">
						Technical details
					</summary>
					<div className="mt-2 flex items-center gap-1">
						<Text as="span" size="xs" textColor="mutedForeground">
							Reference
						</Text>
						<Hash value={hash} />
						<CopyButton value={hash} onCopied={onCopy} />
					</div>
				</details>
			</div>
			{onView && (
				<Button
					variant="outline"
					size="sm"
					onClick={onView}
					className="shrink-0 self-start"
				>
					<Eye className="mr-1 h-3 w-3" aria-hidden="true" />
					View
				</Button>
			)}
		</div>
	);
}
EventCard.displayName = "EventCard";

export default EventCard;
export { EventCard };
